import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { axiosInstance } from "../../lib/axios";

interface Rejection {
  projectId: string;
  projectName: string;
  index: number;
  label: string;
  detail?: string;
  note: string;
  rejectedBy: string;
  rejectedAt?: string | null;
}

// A rejected project requirement blocks delivery, so it is impossible to miss:
// this sits at the top of the BD dashboard until every rejection is resolved.
// Dismissing hides it only for the current session and only for the rejections
// seen so far — a new rejection brings it straight back.
const DISMISS_KEY = "cre-checklist-rejections-dismissed";

export default function ChecklistRejectionAlert() {
  const navigate = useNavigate();
  const [rejections, setRejections] = useState<Rejection[]>([]);
  const [dismissed, setDismissed] = useState<string>(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) || ""; } catch { return ""; }
  });

  useEffect(() => {
    let alive = true;
    const load = () =>
      axiosInstance
        .get("/project/checklist-rejections")
        .then(({ data }) => { if (alive) setRejections(data.rejections || []); })
        .catch(() => { /* the alert is additive — never block the dashboard */ });
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!rejections.length) return null;
  const signature = rejections.map((r) => `${r.projectId}:${r.index}:${r.rejectedAt || ""}`).join("|");
  if (dismissed === signature) return null;

  const dismiss = () => {
    setDismissed(signature);
    try { sessionStorage.setItem(DISMISS_KEY, signature); } catch { /* optional */ }
  };

  return (
    <div className="rounded-2xl border-2 border-red-300 bg-red-50 p-5 shadow-[0_8px_24px_-12px_rgba(220,38,38,0.4)]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-red-100 grid place-items-center shrink-0">
            <AlertTriangle size={20} className="text-red-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[16px] font-extrabold text-red-800">
              {rejections.length} project requirement{rejections.length === 1 ? "" : "s"} rejected by the Project Manager
            </h3>
            <p className="text-[12px] text-red-700/80 mt-0.5">
              Delivery is blocked until each one is fixed and acknowledged again.
            </p>
          </div>
        </div>
        <button onClick={dismiss} className="p-1.5 rounded-full text-red-400 hover:bg-red-100 shrink-0" title="Hide until the next rejection">
          <X size={16} />
        </button>
      </div>

      <div className="mt-4 space-y-2">
        {rejections.slice(0, 6).map((r) => (
          <button
            key={`${r.projectId}-${r.index}`}
            onClick={() => navigate(`/projects?project=${r.projectId}`)}
            className="w-full text-left bg-white border border-red-200 rounded-xl px-4 py-3 hover:border-red-400 transition-colors"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[13px] font-bold text-red-800">{r.label}</span>
              <span className="text-[11px] font-semibold text-red-500">· {r.projectName}</span>
            </div>
            <p className="text-[12px] text-slate-700 mt-1">
              <span className="font-semibold">Reason:</span> {r.note || "No reason given."}
            </p>
            <p className="text-[11px] text-slate-400 mt-1">
              Rejected by {r.rejectedBy}
              {r.rejectedAt ? ` · ${new Date(r.rejectedAt).toLocaleString()}` : ""} — open the project to fix it and
              acknowledge again.
            </p>
          </button>
        ))}
        {rejections.length > 6 && (
          <p className="text-[12px] font-semibold text-red-700">+ {rejections.length - 6} more in the Projects list.</p>
        )}
      </div>
    </div>
  );
}
